import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkles, Clock, GitCompare, Check, WifiOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { isOnline, onNetworkStatusChange, cacheReading, getCachedReadings } from '@/utils/notifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryPage = ({ sessionId }) => {
  const navigate = useNavigate();
  const [readings, setReadings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedReadings, setSelectedReadings] = useState([]);
  const [online, setOnline] = useState(isOnline());

  useEffect(() => {
    const fetchHistory = async () => {
      if (!sessionId) return;

      try {
        const response = await axios.get(`${API}/readings/history/${sessionId}`, { withCredentials: true });
        setReadings(response.data);
        
        // Cache readings for offline access
        response.data.forEach(reading => cacheReading(reading));
        localStorage.setItem('cachedReadingsHistory', JSON.stringify(response.data));
      } catch (error) {
        console.error('Error fetching history:', error);
        
        // Load from cache if offline
        if (!isOnline()) {
          const cached = localStorage.getItem('cachedReadingsHistory');
          if (cached) {
            setReadings(JSON.parse(cached));
            toast.info('Affichage de l\'historique en cache');
          }
        } else {
          toast.error('Erreur lors du chargement de l\'historique');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();

    // Network status listener
    const cleanup = onNetworkStatusChange((status) => {
      setOnline(status);
      if (status) {
        toast.success('Connexion rétablie');
        fetchHistory();
      } else {
        toast.warning('Mode hors-ligne activé');
      }
    });

    const handleVisibilityChange = () => {
      if (!document.hidden && sessionId) {
        fetchHistory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      cleanup();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const toggleReadingSelection = (reading) => {
    if (selectedReadings.find(r => r.id === reading.id)) {
      setSelectedReadings(selectedReadings.filter(r => r.id !== reading.id));
    } else if (selectedReadings.length < 2) {
      setSelectedReadings([...selectedReadings, reading]);
    } else {
      toast.info('Maximum 2 lectures pour la comparaison');
    }
  };

  const handleCompare = async () => {
    if (selectedReadings.length !== 2) {
      toast.error('Sélectionnez exactement 2 lectures');
      return;
    }

    try {
      const response = await axios.post(`${API}/readings/compare`, {
        reading_ids: selectedReadings.map(r => r.id)
      });
      
      navigate('/compare', { state: response.data });
    } catch (error) {
      console.error('Error comparing readings:', error);
      toast.error('Erreur lors de la comparaison');
    }
  };

  const cancelCompareMode = () => {
    setCompareMode(false);
    setSelectedReadings([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Offline Banner */}
        {!online && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3"
          >
            <WifiOff className="h-5 w-5 text-amber-600" />
            <span className="text-sm text-amber-800">
              Mode hors-ligne - Affichage des lectures sauvegardées
            </span>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-to-home-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
          
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4">
                Mon historique
              </h1>
              <p className="text-muted-foreground text-base md:text-lg">
                Retrouvez toutes vos lectures de main précédentes
              </p>
            </div>
            
            {readings.length >= 2 && !compareMode && (
              <Button
                data-testid="compare-mode-btn"
                onClick={() => setCompareMode(true)}
                variant="outline"
                className="rounded-full"
              >
                <GitCompare className="h-4 w-4 mr-2" />
                Comparer
              </Button>
            )}
          </div>
        </motion.div>

        {/* Compare Mode Banner */}
        {compareMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-primary/10 border border-primary/30 rounded-2xl p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <GitCompare className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">Mode comparaison</p>
                <p className="text-sm text-muted-foreground">
                  Sélectionnez 2 lectures ({selectedReadings.length}/2)
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelCompareMode}
                className="rounded-full"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCompare}
                disabled={selectedReadings.length !== 2}
                size="sm"
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Comparer
              </Button>
            </div>
          </motion.div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Sparkles className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : readings.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/50 flex items-center justify-center">
              <Clock className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-2xl mb-4">Aucune lecture pour le moment</h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Commencez votre première lecture de main pour découvrir votre destinée
            </p>
            <Button
              data-testid="start-first-reading-button"
              onClick={() => navigate('/scan')}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Commencer ma première lecture
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {readings.map((reading, index) => {
              const isSelected = selectedReadings.find(r => r.id === reading.id);
              
              return (
                <motion.div
                  key={reading.id}
                  data-testid={`history-item-${reading.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-card p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer ${
                    compareMode && isSelected
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-border'
                  }`}
                  onClick={() => {
                    if (compareMode) {
                      toggleReadingSelection(reading);
                    } else {
                      navigate(`/results/${reading.id}`, { state: { reading } });
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {compareMode ? (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                            isSelected
                              ? 'bg-primary border-primary'
                              : 'bg-secondary/50 border-border'
                          }`}>
                            {isSelected ? (
                              <Check className="h-5 w-5 text-primary-foreground" />
                            ) : (
                              <span className="text-sm font-medium text-muted-foreground">
                                {selectedReadings.length + 1}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-heading text-lg">Lecture du {formatDate(reading.created_at)}</h3>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {reading.selected_themes.map((theme) => (
                              <span
                                key={theme}
                                className="text-xs px-3 py-1 rounded-full bg-secondary/30 text-secondary-foreground capitalize"
                              >
                                {theme}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      {reading.analysis_result?.general_impression && (
                        <p className="text-sm text-muted-foreground line-clamp-2 ml-13">
                          {reading.analysis_result.general_impression}
                        </p>
                      )}
                    </div>
                    
                    {!compareMode && (
                      <Button
                        data-testid={`view-reading-${reading.id}`}
                        variant="outline"
                        size="sm"
                        className="rounded-full flex-shrink-0"
                      >
                        Voir
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* New Reading Button */}
        {readings.length > 0 && !compareMode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-center"
          >
            <Button
              data-testid="new-reading-from-history-button"
              onClick={() => navigate('/scan')}
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Nouvelle lecture
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
